/**
 * Payments page school-year view filter.
 * Classifies receipts by paymentDate only (1 Sep → 30 Jun).
 * Does not mutate payments, charges, classes, or invoices.
 */
import type {Payment} from '../../models'

export const ALL_SCHOOL_YEARS = 'all' as const
export const OUTSIDE_SCHOOL_YEAR = 'outside' as const

export type SchoolYearFilter = string | typeof ALL_SCHOOL_YEARS | typeof OUTSIDE_SCHOOL_YEAR

const YEAR_LABEL = /^(\d{4})-(\d{4})$/
const ISO_DATE_PREFIX = /^(\d{4})-(\d{2})-(\d{2})/

export function isSchoolYearLabel(value: string | null | undefined): value is string {
  if (!value?.trim()) return false
  const m = value.trim().match(YEAR_LABEL)
  if (!m) return false
  return Number(m[2]) === Number(m[1]) + 1
}

export function isSchoolYearFilter(value: string | null | undefined): value is SchoolYearFilter {
  return (
    value === ALL_SCHOOL_YEARS ||
    value === OUTSIDE_SCHOOL_YEAR ||
    isSchoolYearLabel(value)
  )
}

/**
 * Calendar year/month from a payment date without UTC day-shift.
 * Prefers the YYYY-MM-DD prefix on ISO strings (e.g. 2026-09-01T00:00:00.000Z).
 */
export function calendarYearMonth(
  value: string | Date | null | undefined
): {year: number; month: number} | null {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null
    return {year: value.getFullYear(), month: value.getMonth() + 1}
  }
  if (typeof value !== 'string' || !value.trim()) return null
  const prefix = value.trim().match(ISO_DATE_PREFIX)
  if (prefix) {
    const year = Number(prefix[1])
    const month = Number(prefix[2])
    const day = Number(prefix[3])
    if (month < 1 || month > 12 || day < 1 || day > 31) return null
    return {year, month}
  }
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return null
  return {year: parsed.getFullYear(), month: parsed.getMonth() + 1}
}

/** Receipt date used by this view: paymentDate only (not class, charge, or createdAt). */
export function paymentReceiptDate(payment: Pick<Payment, 'paymentDate'>): string | Date | null {
  return payment.paymentDate?.trim() ? payment.paymentDate : null
}

/**
 * School year from the physical payment/receipt date.
 * Sep–Dec → YYYY-(YYYY+1); Jan–Jun → (YYYY-1)-YYYY; Jul/Aug → null.
 */
export function getPaymentSchoolYear(value: string | Date | null | undefined): string | null {
  const parts = calendarYearMonth(value)
  if (!parts) return null
  const {year, month} = parts
  if (month >= 9) return `${year}-${year + 1}`
  if (month >= 1 && month <= 6) return `${year - 1}-${year}`
  return null
}

export function isJulyOrAugustPaymentDate(value: string | Date | null | undefined): boolean {
  const parts = calendarYearMonth(value)
  return !!parts && (parts.month === 7 || parts.month === 8)
}

/** Default selected tab: school year of “today”, or upcoming year in Jul/Aug. */
export function getCurrentAcademicYear(date = new Date()): string {
  const ymd = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate()
  ).padStart(2, '0')}`
  const fromToday = getPaymentSchoolYear(ymd)
  if (fromToday) return fromToday
  const y = date.getFullYear()
  return `${y}-${y + 1}`
}

/** Inclusive receipt-date range for a labeled school year (1 Sep → 30 Jun). */
export function schoolYearDateRange(
  year: SchoolYearFilter
): {startDate: string; endDate: string} | null {
  if (!isSchoolYearLabel(year)) return null
  const start = Number(year.slice(0, 4))
  return {startDate: `${start}-09-01`, endDate: `${start + 1}-06-30`}
}

export function isoDatePrefix(value: string | Date | null | undefined): string | null {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null
    const y = value.getFullYear()
    const m = String(value.getMonth() + 1).padStart(2, '0')
    const d = String(value.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
  }
  if (typeof value !== 'string' || !value.trim()) return null
  const prefix = value.trim().match(ISO_DATE_PREFIX)
  return prefix ? `${prefix[1]}-${prefix[2]}-${prefix[3]}` : null
}

export function isIsoDateInInclusiveRange(
  value: string | Date | null | undefined,
  startDate: string,
  endDate: string
): boolean {
  const day = isoDatePrefix(value)
  return !!day && day >= startDate && day <= endDate
}

/** Always show the current year and the previous year, even before any receipts load. */
export function defaultSchoolYearTabs(currentYear = getCurrentAcademicYear()): string[] {
  const years = [currentYear]
  const m = currentYear.match(YEAR_LABEL)
  if (m) years.push(`${Number(m[1]) - 1}-${m[1]}`)
  return sortAcademicYears(years)
}

export function sortAcademicYears(years: string[]): string[] {
  return [...new Set(years.filter(isSchoolYearLabel))].sort((a, b) => b.localeCompare(a))
}

export function filterPaymentsByReceiptSchoolYear(
  payments: Payment[],
  academicYear: SchoolYearFilter
): Payment[] {
  if (academicYear === ALL_SCHOOL_YEARS) return payments
  if (academicYear === OUTSIDE_SCHOOL_YEAR) {
    return payments.filter((p) => isJulyOrAugustPaymentDate(paymentReceiptDate(p)))
  }
  return payments.filter((p) => getPaymentSchoolYear(paymentReceiptDate(p)) === academicYear)
}

export function sumPaymentTotals(payments: Payment[]): number {
  return roundMoney(payments.reduce((sum, p) => sum + (Number(p.paymentTotal) || 0), 0))
}

export function schoolYearTabsFromPayments(
  payments: Payment[],
  currentYear = getCurrentAcademicYear()
): {years: string[]; hasOutside: boolean} {
  const years = new Set<string>(defaultSchoolYearTabs(currentYear))
  let hasOutside = false
  for (const payment of payments) {
    const date = paymentReceiptDate(payment)
    const year = getPaymentSchoolYear(date)
    if (year) years.add(year)
    else if (isJulyOrAugustPaymentDate(date)) hasOutside = true
  }
  return {years: sortAcademicYears([...years]), hasOutside}
}

export function paymentExportBasename(
  payments: Payment[],
  academicYear: SchoolYearFilter
): string {
  const first = payments[0]
  const user = first?.user
  const rawName =
    user && typeof user === 'object'
      ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
      : first?.guestPayerName || first?.userFirstName || 'paiements'
  const slug = slugifyName(rawName || 'paiements')
  const yearPart =
    academicYear === ALL_SCHOOL_YEARS
      ? 'toutes-les-annees'
      : academicYear === OUTSIDE_SCHOOL_YEAR
        ? 'hors-annee-scolaire'
        : academicYear
  return `paiements-${slug}-${yearPart}`
}

function slugifyName(name: string): string {
  return (
    name
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40) || 'paiements'
  )
}

function roundMoney(n: number): number {
  return Math.round(n * 1000) / 1000
}
